import axios from 'axios';
import { optimizeImageForAPI } from './imageUtils';
import { getDeviceId } from './deviceUtils';
import { getPhotoTimeContext } from './exifUtils';

// API 基础地址
//
// 为什么用相对路径 '/'（重要，别再改回绝对地址）：
//   曾经这里在开发模式下指向 'http://localhost:3000'。结果是——只要不是从
//   「运行后端的那台机器」访问（例如用局域网 IP http://192.168.x.x:3001
//   打开，或用手机访问），浏览器会把 localhost 解析成**访问者自己那台设备**。
//   那上面没有后端，于是所有接口都报 "Network Error"，而页面本身能正常显示
//   （因为静态资源就是本机提供的），排查起来非常费时间。
//
//   用相对路径后，请求发到「当前访问的这个源」：
//     - 开发模式：由 vite.config.js 里的 /api 与 /health 代理转发到后端
//     - 生产模式：由 server.js 代理转发
//   localhost / 127.0.0.1 / 局域网 IP / 手机访问 全部可用。
//
// 关于 __API_URL__：它由 vite.config.js 的 define 注入。这里用 typeof 判断，
// 避免注入失效时抛 ReferenceError 导致整个应用白屏。
const API_BASE_URL =
  (typeof __API_URL__ !== 'undefined' && __API_URL__) ||
  import.meta.env.VITE_API_URL ||
  '/';

/**
 * 把 `/api/xxx` 拼成完整地址。
 *
 * ⚠️ **任何绕开 axios 的请求都必须用它。**
 *
 * 生产环境前端在 CloudBase、API 在 Zeabur，站点根路径下**没有**后端。
 * 裸写 `fetch('/api/xxx')` 在本地开发时因为同源代理能跑通，
 * 一旦分域名就变成向 CloudBase 请求 `/api/xxx` → 404 或返回 HTML，
 * 而错误信息通常只是"解析失败"，很难指向真正的原因。
 *
 * axios 的实例已经带 baseURL，走 `api.post()` 的不需要这个函数。
 *
 * @param {string} path
 * @returns {string}
 */
export function apiUrl(path) {
  const p = String(path || '');
  const withSlash = p.startsWith('/') ? p : `/${p}`;
  return `${API_BASE_URL.replace(/\/+$/, '')}${withSlash}`;
}

console.log('=== API Configuration ===');
console.log('isProduction:', import.meta.env.PROD);
console.log('API_BASE_URL:', API_BASE_URL);
console.log('=========================');

const api = axios.create({
  baseURL: API_BASE_URL,
  // Don't set default Content-Type - let Axios handle it automatically based on data type
  timeout: 180000, // 3 minutes maximum for AI generation (may use fallback)
});

// Add request interceptor to automatically include userId in all requests
api.interceptors.request.use(
  (config) => {
    const userId = getDeviceId();
    // Add userId to headers for all requests
    config.headers['x-user-id'] = userId;
    /*
     * 授权用的设备标识。
     *
     * 服务端靠它把"试用期 / 卡密 / 今日点数"绑到设备上。
     * 用同一个 getDeviceId()，不要另造一个 —— 两套 ID 会让
     * "注册过的设备"和"扣配额的设备"对不上。
     */
    config.headers['x-device-key'] = userId;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 额度/授权被拒时，抛给上层统一处理
 *
 * 402 是"额度用完 / 试用到期"，429 是"太频繁"。
 * 不能都当成普通错误 —— 前者要弹卡密页，后者只要提示等一会。
 * 用自定义事件解耦：任何接口触发都能被顶层的 LicenseGate 接住，
 * 不必在每个调用点写一遍错误处理。
 */
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 402 || (status === 429 && data?.code === 'RATE_LIMITED')) {
      try {
        window.dispatchEvent(
          new CustomEvent('dg:quota-blocked', {
            detail: {
              status,
              code: data?.code,
              message: data?.error || '',
              quota: data?.quota || null,
            },
          })
        );
      } catch {
        /* 事件派发失败不影响原错误继续抛出 */
      }
    }
    return Promise.reject(error);
  }
);

// Story generation
export const generateStory = async (imageFile, useLocation = false, mode = 'story') => {
  console.log('=== generateStory called ===');
  console.log('Image file:', imageFile);
  console.log('Image file size:', imageFile.size);
  console.log('Use location:', useLocation);
  console.log('🎯 Mode:', mode); // 'story' or 'text'

  // ============== 并行处理：图片压缩 + GPS定位 ==============
  // 获取GPS位置的辅助函数
  const getLocationData = async (shouldGetLocation) => {
    if (!shouldGetLocation) {
      console.log('⏭️ 不使用定位信息');
      return null;
    }

    if (!navigator.geolocation) {
      console.log('⚠️ 浏览器不支持GPS定位');
      return null;
    }

    try {
      console.log('📍 正在获取GPS位置（并行处理中）...');
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5分钟内的缓存位置有效
          }
        );
      });

      const locationData = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      };
      console.log('✅ GPS位置获取成功:', locationData);
      return locationData;
    } catch (error) {
      console.log('⚠️ GPS定位失败:', error.message);
      return null;
    }
  };

  // 并行执行：图片压缩 + GPS定位 + 时间上下文提取
  const [optimizedImage, locationData, timeContext] = await Promise.all([
    optimizeImageForAPI(imageFile),
    getLocationData(useLocation),
    getPhotoTimeContext(imageFile)
  ]);

  console.log('✅ 并行处理完成');
  console.log('📷 图片已优化:', (optimizedImage.size / 1024).toFixed(1), 'KB');
  console.log('📍 GPS状态:', locationData ? '已获取' : '未获取/失败');
  console.log('⏰ 时间上下文:', timeContext.description, `(${timeContext.season}, ${timeContext.timeOfDay})`);

  // Get user's Cantonese level from localStorage
  const cantoneseLevel = localStorage.getItem('cantoneseLevel') || '初级';
  const levelMap = {
    '初级': 'beginner',
    '中级': 'intermediate',
    '高级': 'advanced'
  };
  const userLevel = levelMap[cantoneseLevel] || 'beginner';
  console.log('User Cantonese level:', cantoneseLevel, `(${userLevel})`);

  // Get user's voice preference
  const voiceType = localStorage.getItem('voiceType') || 'auto';
  console.log('User voice preference:', voiceType);

  const formData = new FormData();
  formData.append('image', optimizedImage);
  formData.append('userId', getDeviceId());  // 添加设备 ID
  formData.append('level', userLevel);
  formData.append('mode', mode);  // 添加模式参数：'story' 或 'text'
  console.log('✅ Mode added to formData:', mode);

  // 添加GPS位置数据
  if (locationData) {
    formData.append('latitude', locationData.latitude.toString());
    formData.append('longitude', locationData.longitude.toString());
    formData.append('accuracy', locationData.accuracy.toString());
    console.log('✅ GPS数据已添加到请求:', {
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      accuracy: locationData.accuracy
    });
  } else {
    console.log('⚠️ GPS数据未获取到，将在不使用位置信息的情况下生成故事');
  }

  // Only append voiceType if not 'auto'
  if (voiceType !== 'auto') {
    formData.append('voiceType', voiceType);
    console.log('Using manual voice selection:', voiceType);
  } else {
    console.log('Using automatic voice selection');
  }

  // 添加时间上下文信息
  formData.append('timeContext', JSON.stringify(timeContext));
  console.log('✅ 时间上下文已添加:', timeContext.description);

  try {
    console.log('Sending request to /api/generate...');
    const response = await api.post('/api/generate', formData, {
      // Don't set any headers - let Axios handle it automatically
      timeout: 180000, // 3 minutes maximum - fallback may take longer
    });

    console.log('Response received:', response.status);
    console.log('Response data:', response.data);

    return response.data;
  } catch (error) {
    console.error('=== Error in generateStory ===');
    console.error('Error:', error);
    console.error('Error message:', error.message);
    console.error('Error code:', error.code);
    console.error('Error response:', error.response);

    // Re-throw with more detailed error message
    if (error.code === 'ECONNABORTED') {
      throw new Error('请求超时（超过3分钟）。这可能是由于：1) AI模型响应较慢 2) 网络连接问题。请稍后重试。');
    } else if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('生成故事失败，请重试');
    }
  }
};

// Save story to library
export const saveStory = async (storyData) => {
  console.log('=== saveStory called ===');
  console.log('Story data keys:', Object.keys(storyData));
  console.log('API_BASE_URL:', API_BASE_URL);

  try {
    const payload = {
      userId: getDeviceId(),  // 使用真实的设备 ID
      mandarin: storyData.mandarinText,
      cantonese: storyData.cantoneseText,
      cantoneseWords: storyData.cantoneseWords || [],
      audioUrl: storyData.audioUrl,
      imageUrl: storyData.imageUrl,
      imageData: storyData.imageDataBase64, // Include base64 image data
    };
    console.log('Payload size:', JSON.stringify(payload).length, 'bytes');
    console.log('Sending to:', API_BASE_URL + '/api/save');

    const response = await api.post('/api/save', payload);

    console.log('✅ Story saved successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to save story to backend:');
    console.error('Error message:', error.message);
    console.error('Error code:', error.code);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    if (error.request) {
      console.error('Request made but no response received');
      console.error('Request URL:', error.config?.baseURL + error.config?.url);
    }
    // Don't throw error - saving is optional, shouldn't block user experience
    return { success: false, error: error.message };
  }
};

// Recording evaluation
export const evaluateRecording = async (audioFile, originalText, jyutping = '') => {
  console.log('=== evaluateRecording called ===');
  console.log('Audio file:', audioFile);
  console.log('Audio file name:', audioFile.name);
  console.log('Audio file size:', audioFile.size);
  console.log('Audio file type:', audioFile.type);
  console.log('Original text:', originalText);
  console.log('Jyutping:', jyutping || '(未提供 — 本次不会做声调模板对比)');

  const formData = new FormData();
  formData.append('audio', audioFile);
  formData.append('originalText', originalText);
  formData.append('userId', getDeviceId());  // 添加设备 ID

  // 粤拼是「逐字声调纠音」的前提：后端拿它去 Python 端和标准声调模板对比。
  // 没有它，声调准确度只能退化成 ASR 自己报的置信度。
  if (jyutping) {
    formData.append('jyutping', jyutping);
  }

  // Log FormData entries for debugging
  console.log('FormData entries:');
  for (let [key, value] of formData.entries()) {
    console.log(`  ${key}:`, value instanceof File ? `File(${value.name}, ${value.size}, ${value.type})` : value);
  }

  console.log('Sending request to /api/evaluate...');

  try {
    // Use fetch instead of Axios to avoid FormData handling issues
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minutes

    // 走 apiUrl()：生产环境 API 在另一个域名，裸相对路径会打到静态站点上
    const fetchUrl = apiUrl('/api/evaluate');
    console.log('Fetch URL:', fetchUrl);

    const response = await fetch(fetchUrl, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
      // Don't set Content-Type - let browser set it with boundary
    });

    clearTimeout(timeoutId);

    console.log('Response received:', response);
    console.log('Response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error response:', errorData);
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const responseData = await response.json();
    console.log('Response data:', responseData);

    // Map backend response to frontend expected format
    // Backend returns: overallScore, fluencyScore, toneAccuracyScore, etc.
    if (responseData && responseData.data) {
      const data = responseData.data;
      console.log('Mapping data:', data);
      console.log('Available fields:', Object.keys(data));

      // Backend already returns properly named fields
      return {
        success: true,
        data: {
          overallScore: data.overallScore ?? 0,
          fluencyScore: Math.round(data.fluencyScore ?? 0),
          toneAccuracyScore: Math.round(data.toneAccuracyScore ?? 0),
          accuracy: data.accuracy || '',
          similarity: data.similarity ?? 0,
          confidence: data.confidence ?? 0,
          encouragement: data.encouragement || { title: '', message: '', tips: [] },
          userText: data.userText || '',
          originalText: data.originalText || '',
          // Additional v3.0 fields
          rhythmScore: data.rhythmScore ?? 0,
          voiceQualityScore: data.voiceQualityScore ?? 0,
          errorTypes: data.errorTypes || {},
          charMatchRate: data.charMatchRate ?? 0,
          adjustedConfidence: data.adjustedConfidence ?? 0,
          audioAnalysis: data.audioAnalysis || null,
        },
      };
    }

    console.log('Returning raw response data');
    return responseData;
  } catch (error) {
    console.error('=== Error in evaluateRecording ===');
    console.error('Error:', error);
    console.error('Error message:', error.message);
    throw error;
  }
};

// Health check
export const checkHealth = async () => {
  const response = await api.get('/health');
  return response.data;
};

export default api;
